import React from "react";
import {
  X,
  UserPlus,
  Trophy,
  AtSign,
  Heart,
  MessageCircle,
  Bell,
  Church,
  BookOpen,
  Cross,
  HandHeart,
  FileText,
  Loader2,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../../../../services/api";

// ─── Type → icon/color map ────────────────────────────────────────────────────
const iconMap = {
  user_registered: { icon: UserPlus,      bg: "bg-emerald-50", color: "text-emerald-600" },
  comment_on_post: { icon: MessageCircle, bg: "bg-blue-50",    color: "text-blue-600"    },
  community_post:  { icon: FileText,      bg: "bg-indigo-50",  color: "text-indigo-600"  },
  follow:          { icon: UserPlus,      bg: "bg-emerald-50", color: "text-emerald-600" },
  trophy:          { icon: Trophy,        bg: "bg-purple-50",  color: "text-purple-600"  },
  mention:         { icon: AtSign,        bg: "bg-amber-50",   color: "text-amber-600"   },
  like:            { icon: Heart,         bg: "bg-rose-50",    color: "text-rose-500"    },
  comment:         { icon: MessageCircle, bg: "bg-blue-50",    color: "text-blue-600"    },
  system:          { icon: Bell,          bg: "bg-gray-100",   color: "text-gray-500"    },
  prayer:          { icon: HandHeart,     bg: "bg-indigo-50",  color: "text-indigo-600"  },
  church:          { icon: Church,        bg: "bg-sky-50",     color: "text-sky-600"     },
  bible:           { icon: BookOpen,      bg: "bg-amber-50",   color: "text-amber-700"   },
  cross:           { icon: Cross,         bg: "bg-red-50",     color: "text-red-600"     },
};

const getIconMeta = (type) => iconMap[type] || { icon: Bell, bg: "bg-gray-100", color: "text-gray-500" };

// ─── Relative time ────────────────────────────────────────────────────────────
const formatRelativeTime = (dateStr) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1)  return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7)  return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

// ─── Avatar with initials fallback ───────────────────────────────────────────
const BASE_URL = import.meta.env.VITE_API_BASE_URL || "";

const Avatar = ({ src, name }) => {
  const [imgError, setImgError] = React.useState(false);
  const fullSrc = src?.startsWith("http") ? src : `${BASE_URL}${src}`;

  if (!imgError && src && !src.includes("user_default")) {
    return (
      <img
        src={fullSrc}
        alt={name}
        onError={() => setImgError(true)}
        className="w-11 h-11 rounded-full object-cover flex-shrink-0"
      />
    );
  }

  const initials = name
    ?.split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="w-11 h-11 rounded-full bg-[#1F3A60] text-white flex items-center justify-center text-sm font-semibold flex-shrink-0">
      {initials || "?"}
    </div>
  );
};

// ─── Main Modal ───────────────────────────────────────────────────────────────
const NotificationsModal = ({ isOpen, onClose }) => {
  const queryClient = useQueryClient();
  const QUERY_KEY   = "admin-notifications";

  // Fetch only first page, limit 4 — for the dropdown preview
  const { data, isLoading, isError } = useQuery({
    queryKey: [QUERY_KEY, 1],
    queryFn:  () => api.fetchAdminNotifications(1,4),
    enabled:  isOpen,
    keepPreviousData: true,
  });

  const notifications = data?.data || [];
  const unreadCount = data?.unread_count || 0;

  // const unreadCount   = notifications.filter((n) => n.is_read === 0).length;

  // Mark one as read
  const { mutate: markOneRead } = useMutation({
    mutationFn: (id) => api.markNotificationRead(id),
    onSuccess: (_, id) => {
      queryClient.setQueryData([QUERY_KEY, 1], (old) => {
        if (!old) return old;
        return {
          ...old,
          data: old.data.map((n) => (n.id === id ? { ...n, is_read: 1 } : n)),
        };
      });
      // also invalidate the full list page used by AdminNotificationPage
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/30 backdrop-blur-[2px] flex items-center justify-center sm:block"
      onClick={onClose}
    >
      <div
        className="
          sm:absolute sm:right-5 sm:top-[80px]
          relative mx-auto sm:mx-0
          w-[420px] max-w-[calc(100%-2rem)] sm:max-w-none
          max-h-[80vh] sm:max-h-[60vh]
          bg-[#f8fafc] rounded-[14px]
          shadow-[0_20px_60px_rgba(15,29,46,0.18)]
          overflow-hidden border border-[#E8EDF3]
          flex flex-col
        "
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 bg-[#f8fafc] sticky top-0 z-20">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-[17px] font-semibold text-[#0F1D2E]">
                  Notifications
                </h2>
                {unreadCount > 0 && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[#1F3A60] text-white">
                  {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </div>
              <p className="text-[12px] text-[#7B8794] mt-[2px]">
                Stay updated with your community
              </p>
            </div>
            <button onClick={onClose} className="rounded-full flex items-center justify-center hover:bg-[#F7F9FC] transition-all">
              <X size={20} className="text-[#526071]" />
            </button>
          </div>
        </div>

        {/* List */}
        <div
          className="flex-1 overflow-y-auto py-2 notifications-scroll"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          <style>{`.notifications-scroll::-webkit-scrollbar { display: none; }`}</style>

          {/* Loading */}
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-10 gap-3">
              <Loader2 size={24} className="animate-spin text-[#1F3A60]" />
              <p className="text-xs text-[#94A3B8]">Loading notifications…</p>
            </div>
          )}

          {/* Error */}
          {isError && (
            <div className="flex flex-col items-center justify-center py-10 gap-2">
              <Bell size={24} className="text-red-300" />
              <p className="text-xs text-[#94A3B8]">Failed to load notifications</p>
            </div>
          )}

          {/* Empty */}
          {!isLoading && !isError && notifications.length === 0 && (
            <div className="flex flex-col items-center justify-center py-10 gap-2">
              <Bell size={24} className="text-[#94A3B8]" />
              <p className="text-xs text-[#94A3B8]">No notifications yet</p>
            </div>
          )}

          {/* Items */}
          {!isLoading && notifications.map((notif) => {
            const { icon: IconComponent, bg, color } = getIconMeta(notif.type);
            const isUnread = notif.is_read === 0;

            return (
              <div
                key={notif.id}
                // onClick={() => isUnread && markOneRead(notif.id)}
                className={`relative flex items-start gap-4 px-5 py-4 transition-all cursor-pointer hover:bg-[#F8FAFC] ${
                  isUnread ? "bg-[#1F3A60]/[0.03]" : ""
                }`}
              >
                {/* Unread dot */}
                {/* {isUnread && (
                  <div className="absolute left-2 top-7 w-2 h-2 rounded-full bg-[#1F3A60]" />
                )} */}

                {/* Avatar + type icon badge */}
                <div className="relative flex-shrink-0">
                  <Avatar src={notif.avatar} name={notif.name} />
                  <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full ${bg} flex items-center justify-center shadow-sm border border-white`}>
                    <IconComponent size={11} className={color} />
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <p className="text-[13.5px] leading-[21px] text-[#334155]">
                    <span className="font-semibold text-[#0F1D2E]">{notif.name}</span>
                    {" — "}
                    <span>{notif.message}</span>
                  </p>
                  <div className="flex items-center gap-2 mt-1.5">
                    {isUnread && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-medium bg-[#1F3A60]/10 text-[#1F3A60]">
                        New
                      </span>
                    )}
                    <div className="w-1 h-1 rounded-full bg-[#94A3B8]" />
                    <p className="text-[11px] font-medium text-[#94A3B8]">
                      {formatRelativeTime(notif.created_at)}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="bg-[#FAFBFC] px-5 py-4">
          <Link to="/admin/notifications" onClick={onClose}>
            <button className="w-full h-[46px] rounded-2xl bg-[#1F3A60] text-white text-[13px] font-semibold hover:opacity-95 transition-all">
              View All Notifications
            </button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default NotificationsModal;